import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AdjacentType, toTitleCase } from '@iotv/datamodel-core';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton, Stack, Typography, useTheme } from '@mui/material';
import { SimpleTreeView, TreeItem2 } from '@mui/x-tree-view';
import { useEffect, useState } from 'react';
import { getListing, singularArticle } from '../../helpers/format';
import { ActionIcons, TypeIcons } from '../../Iconography';
import { AutosizedTextField } from './AutosizedTextField';
import { ClickableBreadCrumbs } from './BreadCrumbs';
const debug = process.env.REACT_APP_DEBUG && false;
/**
 * Used within a modal dialog to select the context for the app.
 *
 * **History:**
 *
 * This was the precursor to `ObjectTree` — but is specific to customer selection.
 * It has now been refactored into a more flexible `NestedCustomerSelector`.
 * The latest iteration is the `NestedContextSelector` that allows for arbitrary selection of target type(s).
 */
export const NestedContextSelector = ({ nodeEdgeMapper, selectedObject, setSelectedObject, childPaths, targetTypes = ['Customer'], }) => {
    'use no memo';
    // `selectNode()` used before definition
    const theme = useTheme();
    const apexNodes = nodeEdgeMapper.getApexNodes();
    const nodeEdgesHash = Object.keys(nodeEdgeMapper?.skMapped ?? { noNodes: true }).join('');
    useEffect(() => {
        debug && console.log('NestedCustomerSelector has ', { apexNodes, nodeEdgeMapper: nodeEdgeMapper.getContainedItems() });
    }, [nodeEdgesHash]);
    /**
     * Fetches the parents from the {@link nodeEdgeMapper} for use as breadcrumbs
     */
    const getParents = (node) => {
        if (!nodeEdgeMapper)
            return [];
        // const paths = childPaths[node.type as ValidModelType]
        // debug && console.log(`NestedCustomerSelector getParents from '${node.name}' via paths: ${paths?.join(', ')}`)
        // if (!paths) return []
        const parents = nodeEdgeMapper.getAscendents([node]).reverse();
        debug && console.log(`NestedCustomerSelector getParents from '${node.name}' with parents: ${parents?.map((p) => p.name)}`);
        return parents ?? [];
    };
    const renderChild = (child) => (_jsx(TreeItem2, { itemId: child.sk, label: child.name, onClick: () => {
            targetTypes.includes(child.type) && setSelectedObject(child);
            if (targetTypes.includes(child.type) || getChildren(child).length) {
                selectNode(child);
                setSearchText(null);
            }
        }, slots: { iconContainer: TypeIcons[child.type] }, slotProps: {
            content: {
                style: {
                    backgroundColor: child.sk === selectedObject?.sk ? theme.palette.primary.light : undefined,
                    color: child.sk === selectedObject?.sk ? theme.palette.getContrastText(theme.palette.primary.light) : undefined,
                },
            },
        } }, child.sk));
    /**
     * Fetches the adjacent children from the {@link nodeEdgeMapper} via the {@link childPaths}
     */
    const getChildren = (node) => {
        if (!nodeEdgeMapper)
            return [];
        const paths = childPaths[node.type];
        debug && console.log(`NestedCustomerSelector getChildren from '${node.name}' via ${paths?.length ?? 0} paths: ${paths?.join(', ')}`);
        if (!paths)
            return [];
        const children = paths
            .flatMap((path) => nodeEdgeMapper.getNodesByPath(node, path.map((type) => ({ objectTypeId: type, adjacencyType: AdjacentType.CHILD }))))
            .sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0);
        debug && console.log(`NestedCustomerSelector getChildren from '${node.name}' with ${children.length} children: ${children?.map((c) => c.name)}`);
        return children?.map(renderChild);
    };
    const [selectedNode, selectNode] = useState(selectedObject ? (getChildren(selectedObject).length === 0 ? getParents(selectedObject).pop() : selectedObject) : apexNodes[0]);
    const [searchText, setSearchText] = useState(null);
    const [searchChildren, setSearchChildren] = useState(null);
    useEffect(() => {
        const listener = (event) => {
            if (event.key === 'Escape')
                setSearchText(null);
            if (event.code === `Key${event.key.toUpperCase()}`)
                setSearchText((t) => (t === null ? '' : t));
        };
        document.addEventListener('keydown', listener, true);
        return () => document.removeEventListener('keydown', listener, true);
    }, []);
    useEffect(() => {
        if (searchText === null || searchText.length === 0) {
            setSearchChildren(null);
            return;
        }
        const filteredNodes = nodeEdgeMapper.getContainedItems().filter((node) => node.name?.toLocaleLowerCase().includes(searchText.toLocaleLowerCase()));
        setSearchChildren(filteredNodes.map(renderChild));
    }, [searchText]);
    const children = searchChildren ?? (selectedNode ? getChildren(selectedNode) : apexNodes.map((node) => getChildren(node)).flat(1));
    const parents = selectedNode ? getParents(selectedNode) : [apexNodes[0]];
    return (
    // add a miniscule amount of padding due to clipping TextField border on focus
    _jsxs(Stack, { gap: 1, paddingBlockStart: 0.1, children: [_jsxs(Stack, { direction: "row", alignItems: "center", gap: 0.5, children: [_jsx(IconButton, { onClick: () => setSearchText((st) => (st === null ? '' : null)), children: _jsx(ActionIcons.Search, {}) }), searchText === null ? (_jsx(ClickableBreadCrumbs, { descendingNodes: selectedNode ? [...parents, selectedNode] : apexNodes, onClick: (node) => {
                            targetTypes.includes(node.type) && setSelectedObject(node);
                            selectNode(node);
                        }, selectedNode: selectedObject, fontSize: 20 })) : (_jsx(AutosizedTextField, { placeholder: `Search for ${singularArticle(targetTypes[0])} ${getListing(targetTypes.map((type) => toTitleCase(type)), 'one').toLocaleLowerCase()}...`, autoFocus: true, value: searchText, onChange: (e) => setSearchText(e.target.value) }))] }), _jsx(SimpleTreeView, { selectedItems: selectedObject?.sk, slots: { collapseIcon: ActionIcons.ContextMenu, expandIcon: ChevronRightIcon }, children: 0 < children.length ? (children) : (_jsx(TreeItem2, { itemId: "never", disabled: true, label: _jsx(Typography, { fontStyle: "italic", children: "No items to display" }), slotProps: { content: { style: { opacity: 1 } } } })) })] }));
};
